import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,

  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import BookingConfirmDialog from './BookingConfirmDialog';

const STATUS_CONFIG = {
  pending: { color: '#f59e0b', bg: '#fef3c7', labelKey: 'booking.status.pending' },
  confirmed: { color: '#3b82f6', bg: '#dbeafe', labelKey: 'booking.status.confirmed' },
  completed: { color: '#10b981', bg: '#d1fae5', labelKey: 'booking.status.completed' },
  cancelled_by_customer: { color: '#ef4444', bg: '#fee2e2', labelKey: 'booking.status.cancelled_by_customer' },
  cancelled_by_workshop: { color: '#6b7280', bg: '#f3f4f6', labelKey: 'booking.status.cancelled_by_workshop' },
  no_show: { color: '#8b5cf6', bg: '#ede9fe', labelKey: 'booking.status.no_show' },
};

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

export default function BookingDetailDialog({ booking, open, onClose, onActionComplete, customFormFields, t, navigate }) {
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'confirm' | 'reject' | 'cancel' | 'no_show' | 'delete'

  if (!booking) return null;

  const sc = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fi-FI');
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await axios.post(`/api/bookings/${booking.id}/${action}/`);
      const messages = {
        reject: t('booking.actions.rejected'),
        cancel: t('booking.actions.cancelled'),
        no_show: t('booking.actions.marked_no_show'),
      };
      onActionComplete(messages[action] || t('booking.actions.cancelled'), 'success');
      onClose();
    } catch (err) {
      onActionComplete(err.response?.data?.error || 'Error', 'error');
    } finally {
      setLoading(false);
      setConfirmAction(null);
    }
  };

  const handleDelete = () => {
    // Soft cancel
    handleAction('cancel');
  };

  const timeDisplay = booking.booking_mode === 'drop_off' && booking.drop_off_window_name
    ? booking.drop_off_window_name
    : formatTime(booking.booking_time);

  // Resolve custom field labels
  const customFields = customFormFields || [];
  const customValues = booking.custom_field_values || {};
  const customEntries = Object.entries(customValues).filter(([, v]) => v && v !== false);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {booking.service_name || t('booking.detail.title')}
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                px: 1.5,
                py: 0.5,
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: 500,
                bgcolor: sc.bg,
                color: sc.color,
                mt: 0.5,
              }}
            >
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sc.color }} />
              {t(sc.labelKey)}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Booking details */}
          <DetailRow label={t('booking.detail.date')} value={formatDate(booking.booking_date)} />
          {timeDisplay && <DetailRow label={t('booking.detail.time')} value={timeDisplay} />}
          {booking.vehicle_registration && (
            <DetailRow label={t('booking.detail.vehicle')} value={booking.vehicle_registration} />
          )}

          <Box sx={{ my: 0.5 }} />

          {/* Customer info */}
          <DetailRow label={t('booking.detail.customer_name')} value={booking.customer_name} />
          <DetailRow label={t('booking.detail.phone')} value={booking.customer_phone} />
          {booking.customer_email && (
            <DetailRow label={t('booking.detail.email')} value={booking.customer_email} />
          )}
          {booking.customer_notes && (
            <DetailRow label={t('booking.detail.notes')} value={booking.customer_notes} />
          )}

          {/* Extras */}
          {booking.booking_extras?.length > 0 && (
            <>
              <Box sx={{ my: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('booking.detail.extras')}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {booking.booking_extras.map((ex) => (
                  <Chip
                    key={ex.id}
                    label={ex.price_indication ? `${ex.name} (${ex.price_indication})` : ex.name}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </>
          )}

          {/* Custom fields */}
          {customEntries.length > 0 && (
            <>
              <Box sx={{ my: 0.5 }} />
              {customEntries.map(([fieldId, value]) => {
                const fieldDef = customFields.find(f => f.id === fieldId);
                const label = fieldDef?.label || fieldId;
                const displayValue = fieldDef?.type === 'checkbox'
                  ? (value ? t('booking.detail.yes') : t('booking.detail.no'))
                  : String(value);
                return <DetailRow key={fieldId} label={label} value={displayValue} />;
              })}
            </>
          )}

          {/* Meta info */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t('booking.detail.created')}: {new Date(booking.created_at).toLocaleString('fi-FI')}
              {booking.is_new_customer && (
                <Chip label={t('booking.list.new_customer')} size="small" color="info" sx={{ ml: 1, height: 18, fontSize: '10px' }} />
              )}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
          {/* Work order link */}
          {booking.created_work_order && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => { onClose(); navigate(`/maintenances/${booking.created_work_order}`); }}
              sx={{ textTransform: 'none', mr: 'auto' }}
            >
              {t('booking.detail.open_work_order')}
            </Button>
          )}

          {/* Status actions */}
          {booking.status === 'pending' && (
            <>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<CheckCircleIcon />}
                onClick={() => setConfirmAction('confirm')}
                sx={{ textTransform: 'none' }}
                disabled={loading}
              >
                {t('booking.actions.confirm')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setConfirmAction('reject')}
                sx={{ textTransform: 'none' }}
                disabled={loading}
              >
                {t('booking.actions.reject')}
              </Button>
            </>
          )}
          {booking.status === 'confirmed' && (
            <>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => setConfirmAction('cancel')}
                sx={{ textTransform: 'none' }}
                disabled={loading}
              >
                {t('booking.actions.cancel')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<PersonOffIcon />}
                onClick={() => setConfirmAction('no_show')}
                sx={{ textTransform: 'none' }}
                disabled={loading}
              >
                {t('booking.actions.no_show')}
              </Button>
            </>
          )}

          {/* Delete (soft cancel) — for pending/confirmed only, not already cancelled */}
          {!['cancelled_by_customer', 'cancelled_by_workshop', 'completed', 'no_show'].includes(booking.status) && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmAction('delete')}
              sx={{ textTransform: 'none' }}
              disabled={loading}
            >
              {t('Delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirm has its own dialog: the time can be adjusted before the customer is told */}
      <BookingConfirmDialog
        booking={booking}
        open={confirmAction === 'confirm'}
        onClose={() => setConfirmAction(null)}
        onActionComplete={(message, severity) => {
          onActionComplete(message, severity);
          if (severity === 'success') onClose();
        }}
      />

      {/* Confirmation dialog for the other actions */}
      <Dialog open={!!confirmAction && confirmAction !== 'confirm'} onClose={() => setConfirmAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmAction === 'delete' ? t('booking.detail.delete_title') : t(`booking.actions.${confirmAction}_title`)}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction === 'delete' ? t('booking.detail.delete_message') : t(`booking.actions.${confirmAction}_message`)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={loading}>{t('Cancel')}</Button>
          <Button
            onClick={() => confirmAction === 'delete' ? handleDelete() : handleAction(confirmAction)}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (confirmAction === 'delete' ? t('Delete') : t(`booking.actions.${confirmAction}`))}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
